import { createContext, useContext, type ReactNode } from "react";

export interface PageOpsContextValue {
  saveCurrentPageToStore: () => void;
  loadPageToHooks: (pageData: any) => void;
  handleMapUpload: () => void;
  handleTokenUpload: () => void;
  pageCallbacks: {
    create: (name: string, parentPageId?: string) => void;
    rename: (pageId: string, name: string) => void;
    /** movePlayersTo: where the players go when the page is theirs (the server refuses without it). */
    delete: (pageId: string, movePlayersTo?: string) => void;
    /**
     * «Show to players…»: a confirmation naming the map, then page:activate
     * and the message with «back to the previous map». The only way a table
     * surface moves the players to a map.
     */
    askShow: (pageId: string) => void;
    preview: (pageId: string) => void;
    returnToActive: () => void;
    reorder: (pageIds: string[]) => void;
  };
}

const PageOpsContext = createContext<PageOpsContextValue | null>(null);

export function PageOpsProvider({ value, children }: { value: PageOpsContextValue; children: ReactNode }) {
  return (
    <PageOpsContext.Provider value={value}>
      {children}
    </PageOpsContext.Provider>
  );
}

export function usePageOpsContext(): PageOpsContextValue {
  const ctx = useContext(PageOpsContext);
  if (!ctx) {
    throw new Error("usePageOpsContext must be used within a PageOpsProvider");
  }
  return ctx;
}

/**
 * Мягкий вариант для поверхностей вне PageOpsProvider. Провайдер живёт внутри
 * AppShell (его собирает PageManager), а GM Studio рендерится ВМЕСТО AppShell —
 * жёсткий хук там роняет всё дерево в ErrorBoundary (пойман живым смоуком
 * studio-smoke.spec, 2026-07-23). Активация/превью страниц из Studio — бонус
 * при наличии контекста; подъём PageOps выше StudioGate — отдельная задача.
 */
export function usePageOpsContextOptional(): PageOpsContextValue | null {
  return useContext(PageOpsContext);
}
